import type { AxiosPromise, CancelTokenSource } from 'axios'
import type { UploadFile } from 'element-plus'

import type { DeviceVisibilityModel } from '@/api/adapters/deviceAccessAdapter'
import type { GeolocationApiModel, LeafletGeolocationApiModel } from '@/types/GeolocationModel'
import type { Nullable } from '@/types/Nullable'
import type { StoreModel } from '@/types/StoreModel'
import request from '@/utils/request'

export type LeafletStatus = 'READY_FOR_ACTIVATION' | 'READY_FOR_DISACTIVATION' | 'ACTIVE' | 'INACTIVE' | 'DELETED'
export type LeafletDevices = 'ANDROID' | 'IOS' | 'WP' | 'WEB' | 'MPROMOCJE' | 'UA_LIGHT_OFFERS'
export type LeafletAreasState = 'PENDING' | 'GENERATED' | 'FAILED'
export type LeafletGeolocationType = 'STORE' | 'LEAFLET'

export interface LeafletUpdateQueryParams {
  'old-natural-id'?: string
  'copy-folder'?: boolean
  'watermark'?: boolean
}

const LEAFLETS_API = '/api/leaflets'

export type AllLeafletsPredefinedFilter = {
  predefinedFilter: 'LEAFLET_DASHBOARD' | 'LEAFLET_XML_ORDERING'
}

export type LeafletFormPayload = {
  id: Nullable<string>
  validFrom: string
  validTo: string
  untilEndOfSupply: boolean
  publishDate: string
  publicationEndTime: Nullable<string>
  additionalInfo: string
  additionalInfo2: string
  longUrls: string[]
  thumbnailTrackingUrl: string
  hasRecipe: boolean
  status: LeafletStatus
  redirectURL: string
  zipCodes: string
  pdfURL: Nullable<string>
  imagesURLPattern: Nullable<string>
  numberOfImages: Nullable<number>
  generateLastPageInsert: boolean
  notShowAdInserts: boolean
  forceLast: boolean
  notAllowShoppingList: boolean
  androidPriority: Nullable<number>
  android2Priority: Nullable<number>
  iosPriority: Nullable<number>
  webPriority: Nullable<number>
  wpPriority: Nullable<number>
  groupId: unknown
  scheduledLongLink: { date: Nullable<string>; time: Nullable<string>; longUrls: string[]; devices: string[] }
  scheduledThumbnailTrackingLink: {
    date: Nullable<string>
    time: Nullable<string>
    thumbnailURL: string
    devices: string[]
  }
  longUrlDevices: string[]
  thumbnailTrackingUrlDevices: string[]
  naturalId: string
  firstPageFile: UploadFile[]
  showOnAndroid?: boolean
  showOnAndroid2?: boolean
  showOnIOS?: boolean
  showOnWeb?: boolean
  showOnWP?: boolean
  geolocations: LeafletGeolocationApiModel[]
  leafletLocationTypes: LeafletGeolocationType[]
  shouldShowToUserWithNoLocation: boolean
}

export type LeafletLinksPayload = {
  showOnlineBadge: boolean
  longUrls: string[]
  longUrlDevices: string[]
  thumbnailTrackingUrl: string
  thumbnailTrackingUrlDevices: string[]
}

export function getAllLeaflets(
  params: AllLeafletsPredefinedFilter,
  cancelToken: Nullable<CancelTokenSource>
): AxiosPromise<LeafletApiModel[]> {
  return request.get<LeafletApiModel[]>(LEAFLETS_API, { params, cancelToken: cancelToken?.token })
}

export function getLeafletById(leafletId: string): AxiosPromise<LeafletApiModel> {
  return request.get<LeafletApiModel>(`${LEAFLETS_API}/${leafletId}`)
}

export function addLeafletFiles(
  data: FormData,
  leafletId: LeafletApiModel['id'],
  watermark: boolean
): AxiosPromise<void> {
  const query = watermark ? '?watermark=true' : ''

  return request.post(`${LEAFLETS_API}/${leafletId}/files${query}`, data)
}

export function addLeafletCover(data, leafletId): AxiosPromise<void> {
  return request.post(`${LEAFLETS_API}/${leafletId}/first-page`, data)
}

export function putLeaflet(
  data: LeafletFormPayload,
  storeId: StoreModel['id'],
  queryParams?: LeafletUpdateQueryParams
): AxiosPromise<void> {
  const params = new URLSearchParams()
  params.set('storeId', storeId)

  if (queryParams?.['old-natural-id']) {
    params.set('old-natural-id', queryParams['old-natural-id'])
  }
  if (queryParams?.['copy-folder']) {
    params.set('copy-folder', 'true')
  }
  if (queryParams?.watermark) {
    params.set('watermark', 'true')
  }

  return request.put(`${LEAFLETS_API}/${data.id}?${params.toString()}`, data)
}

export function updateLeafletOnlineBadge(data) {
  return request({
    url: `${LEAFLETS_API}/${data.id}`,
    method: 'patch',
    data
  })
}

export function updateLeafletLinks(leafletId: LeafletApiModel['id'], data: LeafletLinksPayload): AxiosPromise<void> {
  return request({
    url: `${LEAFLETS_API}/${leafletId}`,
    method: 'patch',
    data
  })
}

export function removeLeaflet(leafletId, storeId) {
  return request({
    url: `${LEAFLETS_API}/${leafletId}?storeId=${storeId}`,
    method: 'delete'
  })
}

export function fetchPageDetailsImages(leafletId): Promise<{ data: LeafletPageImageDetails[] }> {
  return request({
    url: `${LEAFLETS_API}/${leafletId}/images`,
    method: 'get'
  })
}

export function refreshLeafletDate(leafletId) {
  return request({
    url: `${LEAFLETS_API}/${leafletId}/refresh`,
    method: 'post'
  })
}

export function getArchivedLeaflets(query) {
  return request({
    url: `${LEAFLETS_API}/archived?${query}`,
    method: 'get'
  })
}

export function fetchLeafletDetails(leafletId: LeafletApiModel['id']) {
  return request.get<LeafletDetailsApiModel>(`${LEAFLETS_API}/${leafletId}/details`)
}

export function deleteLinksPerStore(query) {
  return request({
    url: `${LEAFLETS_API}/urls?${query}`,
    method: 'delete'
  })
}

export function getLeafletHistory(leafletId) {
  return request({
    url: `/api/history/leaflets/${leafletId}`,
    method: 'get'
  })
}

export function getRedirectsHistory(leafletId) {
  return request({
    url: `/api/history/leaflets/${leafletId}/redirects`,
    method: 'get'
  })
}

export enum SocialMediaPlatforms {
  YOUTUBE = 'YOUTUBE',
  TIKTOK = 'TIKTOK'
}

export interface LeafletSocialMediaLink {
  platform: SocialMediaPlatforms
  url: string
}

interface LeafletSocialMediaApiLink {
  id: string
  platform: SocialMediaPlatforms
  url: string
}

export interface LeafletDetailsApiModel {
  id: string
  naturalId: string
  seoId: string
  thumbnail: string
  storeName: string
  additionalInfo: string
  validFrom: string
  validTo: string
  numberOfPages: number
  publishDate: string
  createdAt: string
  thumbnailTrackingUrl: string
  redirectURL: string
  zipCodes: string
  notShowAdInserts: boolean
  status: LeafletStatus
  refreshedBy: string
  createdBy: string
  areasState: LeafletAreasState
  showOnAndroid?: boolean
  showOnAndroid2?: boolean
  showOnIOS?: boolean
  showOnWeb?: boolean
  showOnWP?: boolean
  socialMediaLinks: LeafletSocialMediaApiLink[]
  geolocations: GeolocationApiModel[]
  leafletLocationTypes: LeafletGeolocationType[]
  shouldShowToUserWithNoLocation: boolean
}

export type LeafletDetailsModel = Omit<
  LeafletDetailsApiModel,
  'showOnAndroid' | 'showOnAndroid2' | 'showOnIOS' | 'showOnWeb' | 'showOnWP'
> &
  DeviceVisibilityModel

export interface LeafletApiModel {
  id: string
  storeId: string
  naturalId: string
  seoId: string
  validFrom: string // date
  validTo: string // date
  publishDate: string // date-time
  publicationEndTime: string // date-time
  additionalInfo: string
  additionalInfo2: string
  longUrls: string[]
  thumbnailTrackingUrl: string
  redirectURL: string
  zipCodes: string
  status: LeafletStatus
  thumbnail: string
  numberOfPages: number
  createdAt: string // date-time
  showOnAndroid: boolean
  showOnAndroid2: boolean
  showOnIOS: boolean
  showOnWP: boolean
  showOnWeb: boolean
  shouldShowToUserWithNoLocation: boolean
  generateLastPageInsert: boolean
  untilEndOfSupply: boolean
  notAllowShoppingList: boolean
  notShowAdInserts: boolean
  activateReadyStatusAt: string // date-time
  endOfSupplyMessage: string
  androidPriority: number
  android2Priority: number
  iosPriority: number
  wpPriority: number
  webPriority: number
  category: string
  forceLast: boolean
  groupId: string
  groupName: string
  showOnlineBadge: boolean
  showPopularBadge: boolean
  hasRecipe: boolean
  hasArticles: boolean
  replaceValidityText: string
  longUrlDevices: LeafletDevices[]
  thumbnailTrackingUrlDevices: LeafletDevices[]
  refreshedBy: string
  createdBy: string
  areasState: LeafletAreasState
  pages: Array<{
    url: string
    width: number
    height: number
    pageNumber: number
  }>
  socialMediaLinks: LeafletSocialMediaApiLink[]
  geolocations: GeolocationApiModel[]
  leafletLocationTypes: LeafletGeolocationType[]
}

export interface LeafletModel {
  id: string
  storeId: string
  naturalId: string
  seoId: string
  validFrom: string // date
  validTo: string // date
  publishDate: string // date-time
  publicationEndTime: string // date-time
  additionalInfo: Nullable<string>
  additionalInfo2: Nullable<string>
  longUrls: string[]
  thumbnailTrackingUrl: string
  redirectURL: string
  zipCodes: string
  status: LeafletStatus
  thumbnail: string
  numberOfPages: number
  createdAt: string // date-time
  isVisibleOnAndroid: boolean
  isVisibleOnAndroid2: boolean
  isVisibleOnIos: boolean
  isVisibleOnWeb: boolean
  isVisibleOnWp: boolean
  shouldShowToUserWithNoLocation: boolean
  generateLastPageInsert: boolean
  untilEndOfSupply: boolean
  notAllowShoppingList: boolean
  notShowAdInserts: boolean
  activateReadyStatusAt: string // date-time
  endOfSupplyMessage: string
  androidPriority: number
  android2Priority: number
  iosPriority: number
  wpPriority: number
  webPriority: number
  category: string
  forceLast: boolean
  groupId: string
  groupName: string
  showOnlineBadge: boolean
  showPopularBadge: boolean
  hasRecipe: boolean
  hasArticles: boolean
  replaceValidityText: string
  longUrlDevices: LeafletDevices[]
  thumbnailTrackingUrlDevices: LeafletDevices[]
  refreshedBy: string
  createdBy: string
  areasState: LeafletAreasState
  pages: Array<{
    url: string
    width: number
    height: number
    pageNumber: number
  }>
  socialMediaLinks: Array<{
    id: string
    platform: SocialMediaPlatforms
    url: string
  }>
  geolocations: GeolocationApiModel[]
  leafletLocationTypes: LeafletGeolocationType[]
}

export interface LeafletPageImageDetails {
  url: string
  size: string
  dimensions: string
  pageNumber: number
}
